import type {
    BidListItemModel,
    FullNameSplitModel,
    OrganizationWaybillMatchingSettingEditModel,
    TitleGetModel,
    WaybillBidModel,
    WaybillBidMatchingLogViewModel,
} from "api";
import { bidStatusesEnum, waybillTitleStatusesEnum, waybillTitleTypesEnum } from "helpers/enums";
import { OdataFilters, queryCollection } from "helpers/reqparams";
import { alert } from "services/alert";
import { alertRequest, body, request } from "services/request";

const resource = "api/waybills";
const bidsResource = "api/bids";

export type WaybillTitleType = (typeof waybillTitleTypesEnum)[keyof typeof waybillTitleTypesEnum];
export type WaybillTitleStatus = (typeof waybillTitleStatusesEnum)[keyof typeof waybillTitleStatusesEnum];

export interface GenerateChangeTitleItemsModel {
    carId?: number | null;
    trailerId?: number | null;
    driverId?: number | null;
}

export interface WaybillSignatoryModel {
    lastName: string;
    firstName: string;
    patronymic?: string | null;
    position?: string | null;
}

export interface GenerateChangeTitleModel {
    waybillId: number;
    itemsData?: GenerateChangeTitleItemsModel | null;
    replacementDateTime?: string | null;
    replacementReasonText: string;
    signatory: WaybillSignatoryModel;
    powerOfAttorneyId?: number | null;
}

export interface WaybillListParticipant {
    fullName?: string | null;
    inn?: string | null;
    kpp?: string | null;
    ogrnip?: string | null;
    foreignerType?: string | null;
    country?: string | null;
    foreignerId?: string | null;
    foreignerDocumentType?: string | null;
    foreignerDocumentNumber?: string | null;
    foreignerDocumentDate?: string | null;
    address?: string | null;
    phoneNumber?: string | null;
    email?: string | null;
    otherContacts?: string | null;
    otherInfo?: string | null;
    about?: string | null;
}

export interface WaybillListModel {
    id: number;
    billNumber: string;
    driverName: string;
    carNumber: string;
    billDate: string;
    lastTitleType?: WaybillTitleType | null;
    lastTitleStatus?: WaybillTitleStatus | null;
    cargoOwner?: WaybillListParticipant | null;
    consignee?: WaybillListParticipant | null;
    carrier?: WaybillListParticipant | null;
    bidId?: number | null;
    changeTitleRequired?: boolean;
    carLogist?: FullNameSplitModel | null;
}

export interface BidWaybillListItemModel {
    id: number;
    bidId?: number | null;
    billNumber?: string;
    lastTitleType?: WaybillTitleType | null;
    lastTitleStatus?: WaybillTitleStatus | null;
    lastTitleSignatureFileId?: number | null;
}

export type WaybillContentValue = string | number | boolean | null | WaybillContent | WaybillContentValue[];

export interface WaybillContent {
    [key: string]: WaybillContentValue | undefined;
    billDate?: string;
    billNumber?: string;
    bidDate?: string | null;
    bidNumber?: string | null;
    unloadComment?: string | null;
}

export interface WaybillTitleModel {
    id: number;
    type: WaybillTitleType;
    receivedAt: string;
    signedAt?: string | null;
    signed: boolean;
    status: WaybillTitleStatus;
    remarks?: WaybillContent | null;
    rejectionReason?: string | null;
}

export interface WaybillViewModel {
    id: number;
    intStateFlags: number;
    externalLink?: string | null;
    bidId?: number | null;
    lastTitleId?: number | null;
    content: WaybillContent;
    titles: WaybillTitleModel[];
    changeTitleRequired?: boolean;
    carLogist?: FullNameSplitModel | null;
}

export interface WaybillArchiveStatusApplyModel {
    waybillId: number;
    archive: boolean;
}

interface BidSelectParams {
    start?: number;
    length?: number;
    filters?: Array<{ dataIndex: string; value: string | number | Array<string | number> }>;
}

const showLoadError = (message: string) => {
    alert({ type: "error", title: "Ошибка", message });
    return false;
};

export const index = ({ preparedFilter, ...params }: OdataFilters) => {
    const query = queryCollection(params, { filter: preparedFilter, orderBy: "billDate desc" });
    return request(`${resource}/getList${query}`, { method: "GET" }, { countInHeaders: true })
        .then((response) => (response === false ? showLoadError("Не удалось загрузить реестр ЭТрН.") : response))
        .catch(() => false);
};

export const getItem = (waybillId: number | string): Promise<WaybillViewModel | false> =>
    request(`${resource}/get?waybillId=${waybillId}`, { method: "GET" });

export const getListByBid = (bidId: number): Promise<WaybillBidModel[] | false> =>
    request(`${resource}/getListByBid/${bidId}`, { method: "GET" });

export const applyArchiveStatus = (params: WaybillArchiveStatusApplyModel): Promise<boolean> =>
    request(`${resource}/applyArchiveStatus`, body(params)).then(Boolean);

const getApplyErrorMessage = (errorText: string) => {
    try {
        const parsed = JSON.parse(errorText);
        return parsed.message || errorText;
    } catch (_error) {
        return errorText;
    }
};

export const applyBid = (params: { waybillId: number; bidId: number | null }): Promise<boolean> =>
    request(`${resource}/applyBid`, body(params), { withoutErrorAlert: true, returnErrorText: true }).then(
        (response) => {
            if (response?.errorText) {
                alert({
                    type: "error",
                    title: "Не удалось изменить связь с заявкой",
                    message: getApplyErrorMessage(response.errorText),
                });
                return false;
            }
            if (response === false) {
                alert({ type: "error", title: "Ошибка", message: "Не удалось изменить связь ЭТрН с заявкой." });
                return false;
            }
            alert({ type: "success", title: "Готово", message: "Связь ЭТрН с заявкой обновлена." });
            return true;
        },
    );

const allowedBidStatuses = [
    bidStatusesEnum.New,
    bidStatusesEnum.Planned,
    bidStatusesEnum.Started,
    bidStatusesEnum.Done,
];

export const getAvailableBids = (params: BidSelectParams): Promise<BidListItemModel[]> => {
    const idFilter = params.filters?.find(({ dataIndex }) => dataIndex === "id")?.value;
    const requestedIds = (Array.isArray(idFilter) ? idFilter : [idFilter]).reduce<number[]>((result, value) => {
        const id = Number(value);
        if (Number.isInteger(id) && id > 0) {
            result.push(id);
        }
        return result;
    }, []);
    const requestedCurrentBid = Array.isArray(idFilter) && requestedIds.length > 0;
    const filter = requestedCurrentBid
        ? { id: { in: requestedIds } }
        : {
              isDeleted: false,
              status: { in: allowedBidStatuses },
              ...(requestedIds.length > 0 ? { id: requestedIds[0] } : {}),
          };
    const query = queryCollection(
        { start: params.start, length: params.length, filters: [] },
        { filter, orderBy: "id desc" },
    );
    return request(`${bidsResource}/getList${query}`, { method: "GET" })
        .then((response) => (Array.isArray(response) ? response : []))
        .catch(() => []);
};

export const generateChangeTitle = (params: GenerateChangeTitleModel): Promise<TitleGetModel> =>
    alertRequest(`${resource}/generateChangeTitle`, body(params));

export const getChangeTitlePreview = (params: GenerateChangeTitleModel): Promise<unknown> =>
    request(`${resource}/getChangeTitlePreview`, body(params));

export const getSettings = (): Promise<OrganizationWaybillMatchingSettingEditModel | false> =>
    request(`${resource}/getSettings`, { method: "GET" });

export const applySettings = (params: OrganizationWaybillMatchingSettingEditModel): Promise<boolean> =>
    alertRequest(`${resource}/applySettings`, body(params));

export const getMatchingLogs = (waybillId: number | string): Promise<WaybillBidMatchingLogViewModel[] | false> =>
    request(`${resource}/GetMatchingLogs?waybillId=${encodeURIComponent(String(waybillId))}`, { method: "GET" });
